/** What a code release will do, before it is approved (tenant retirement CONTRACT §8.7,
 *  `TenantReleasePreview`): the placeholder codes, the imported ids it re-keys per model, the API
 *  keys it revokes, the sandbox's production-key entries it renames, the connections it unlinks,
 *  and the state stored under the code it purges, moves or leaves (per key family; families with
 *  nothing stored are counted, not listed). Counts are estimates — the job does the exact work.
 *  Whatever blocks the release now is listed last; the approve dialog keeps Approve off for it. */
import {
  Alert,
  AlertTitle,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';

import type { TenantReleaseCodeState, TenantReleasePreview } from '@/api/tenantLifecycle';

import { LifecycleBlockerList } from './LifecycleBlockerList';

const MONO = { fontFamily: 'monospace' } as const;

const ACTION_WORDS: Record<TenantReleaseCodeState['action'], string> = {
  purge: 'Purge',
  move: 'Move',
  leave: 'Leave to expire',
};

/** `demo.Group_Tour` → `Group_Tour`. */
const modelName = (key: string) => key.replace(/^[a-z0-9_]+\./i, '');

const total = (counts: Record<string, number> | null | undefined) =>
  Object.values(counts ?? {}).reduce((n, c) => n + c, 0);

function countWords(row: TenantReleaseCodeState): string {
  if (row.count == null) return row.count_capped ? 'found when it runs' : 'unknown';
  return row.count_capped ? `${row.count}+` : String(row.count);
}

/** Per model, biggest first: "Group_Tour 120 · Activity 30". */
function perModel(counts: Record<string, number> | null | undefined): string {
  return Object.entries(counts ?? {})
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([model, n]) => `${modelName(model)} ${n}`)
    .join(' · ');
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '200px minmax(0,1fr)' }, gap: 1 }}>
      <Typography component="dt" variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>
        {children}
      </Typography>
    </Box>
  );
}

export function ReleasePreviewPanel({ topId, preview }: { topId: number; preview: TenantReleasePreview }) {
  const ids = total(preview.ext_ids);
  const twinIds = total(preview.twin_ext_ids);
  // A family with nothing stored is not worth a row; one the preview cannot count is.
  const rows = preview.code_state.filter((r) => r.count !== 0);
  const empty = preview.code_state.length - rows.length;
  return (
    <Box role="region" aria-label="What the release does" sx={{ display: 'grid', gap: 1.5 }}>
      <Typography variant="body2">
        The code{' '}
        <Box component="strong" sx={MONO}>
          {preview.code}
        </Box>{' '}
        becomes{' '}
        <Box component="strong" sx={MONO}>
          {preview.tombstone}
        </Box>
        {preview.twin_code && preview.twin_tombstone && (
          <>
            ; its sandbox{' '}
            <Box component="strong" sx={MONO}>
              {preview.twin_code}
            </Box>{' '}
            becomes{' '}
            <Box component="strong" sx={MONO}>
              {preview.twin_tombstone}
            </Box>
          </>
        )}
        .
      </Typography>
      <Box component="dl" sx={{ m: 0, display: 'grid', gap: 0.5 }}>
        <Fact label="Imported ids re-keyed">
          {ids}
          {twinIds > 0 && `, and ${twinIds} of the sandbox`}
          {ids > 0 && (
            <Box component="span" sx={{ display: 'block', color: 'text.secondary' }}>
              {perModel(preview.ext_ids)}
            </Box>
          )}
        </Fact>
        <Fact label="API keys revoked">{preview.api_keys}</Fact>
        <Fact label="Production-key entries renamed">{preview.production_key_entries}</Fact>
        <Fact label="Connections unlinked">{preview.restech_connections}</Fact>
      </Box>
      {rows.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Stored under the code">
            <TableHead>
              <TableRow>
                <TableCell>Action</TableCell>
                <TableCell>Keys or prefix</TableCell>
                <TableCell>Where</TableCell>
                <TableCell align="right">Count</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={`${r.code}:${r.name}`} data-family={r.name}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{ACTION_WORDS[r.action]}</TableCell>
                  <TableCell sx={{ ...MONO, overflowWrap: 'anywhere' }}>{r.key_or_prefix}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.alias}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {countWords(r)}
                    {r.refused > 0 && (
                      <Box component="span" sx={{ display: 'block', color: 'text.secondary' }}>
                        {r.refused} left: may be another tenant’s
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      <Typography variant="caption" color="text.secondary">
        {rows.length === 0 && preview.code_state.length > 0
          ? 'Nothing is stored under the code now. '
          : empty > 0
            ? `${empty} more key ${empty === 1 ? 'family has' : 'families have'} nothing stored. `
            : ''}
        Counts are estimates; the job does the exact work.
      </Typography>
      {preview.blockers.length > 0 && (
        <Alert severity="warning">
          <AlertTitle>What blocks the release now</AlertTitle>
          <LifecycleBlockerList
            topId={topId}
            blockers={preview.blockers}
            label="What blocks the release now"
          />
        </Alert>
      )}
    </Box>
  );
}
